import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import NewsCard from '@/components/feature/NewsCard';
import Pagination from '@/components/base/Pagination';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { news } from '@/mocks/news';
import { getAllNewsTypes, paginate, totalPages } from '@/utils/media';

const PAGE_SIZE = 10;

export default function NewsList() {
  usePageMeta(
    '影视资讯 - 最新影视动态与行业新闻 - 嫩草影院',
    '嫩草影院影视资讯频道，汇集最新电影新闻、电视剧动态与行业趋势，帮你第一时间掌握影视圈热点。',
    '影视资讯,电影新闻,电视剧新闻,行业动态,嫩草影院',
  );

  const [activeType, setActiveType] = useState('全部');
  const [page, setPage] = useState(1);
  const [loadCount, setLoadCount] = useState(PAGE_SIZE);
  const isMobile = useMediaQuery('(max-width: 767px)');

  const types = useMemo(() => ['全部', ...getAllNewsTypes()], []);

  const filtered = useMemo(() => {
    const list = news.slice().sort((a, b) => b.publishTime.localeCompare(a.publishTime));
    if (activeType === '全部') return list;
    return list.filter((n) => n.type === activeType);
  }, [activeType]);

  useEffect(() => {
    setPage(1);
    setLoadCount(PAGE_SIZE);
  }, [activeType]);

  const pages = totalPages(filtered.length, PAGE_SIZE);
  const pageItems = paginate(filtered, page, PAGE_SIZE);
  const mobileItems = filtered.slice(0, loadCount);

  return (
    <div className="flex min-h-screen flex-col bg-background-950">
      <Navbar />

      <main className="flex-1">
        <section className="border-b border-background-800 bg-background-900/50">
          <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-400">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-300">影视资讯</span>
            </nav>
            <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-50 md:text-3xl">
              影视资讯
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-foreground-400">
              最新电影新闻、电视剧动态与行业趋势，第一时间掌握影视圈热点。
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {types.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setActiveType(type)}
                  className={`min-h-9 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    activeType === type
                      ? 'bg-primary-500 font-medium text-background-950'
                      : 'bg-background-800 text-foreground-300 hover:bg-background-700 hover:text-foreground-50'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="mx-auto w-full px-4 py-8 md:px-6">
          <p className="mb-4 text-sm text-foreground-400">
            共 <span className="font-bold text-primary-400">{filtered.length}</span> 条资讯
          </p>

          {filtered.length > 0 ? (
            <>
              <div className="hidden md:block">
                <div className="rounded-lg border border-background-800 bg-background-900 px-5 py-2">
                  {pageItems.map((item) => (
                    <NewsCard key={item.id} item={item} />
                  ))}
                </div>
              </div>

              <div className="md:hidden">
                <div className="flex flex-col gap-4">
                  {mobileItems.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-lg border border-background-800 bg-background-900 px-4"
                    >
                      <NewsCard item={item} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 hidden md:block">
                <Pagination
                  current={page}
                  total={pages}
                  onChange={(p) => {
                    setPage(p);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              </div>

              <div className="mt-6 md:hidden">
                {loadCount < filtered.length ? (
                  <button
                    type="button"
                    onClick={() => setLoadCount((c) => c + PAGE_SIZE)}
                    className="flex h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-700 text-sm font-medium text-foreground-200 transition-colors hover:border-primary-600 hover:text-primary-400"
                  >
                    <i className="ri-arrow-down-line" />
                    加载更多
                  </button>
                ) : (
                  <p className="text-center text-xs text-foreground-500">已经到底啦</p>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-background-800 bg-background-900 px-6 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-800 text-foreground-500">
                <i className="ri-newspaper-line text-3xl" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-foreground-100">
                暂无相关资讯
              </h3>
              <p className="mt-2 text-sm text-foreground-400">换个分类看看吧。</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}